import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;
const markAttendanceBaseUrl = `${window.location.origin}/mark-attendance`;

function AdminAttendanceQR() {
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchActive = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/attendance-qr/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setActive(response.data.active || null);
      } else {
        setError(response.data.message || 'Failed to fetch QR code.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching QR code.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActive();
  }, [fetchActive]);

  const handleGenerate = async () => {
    if (active) {
      const confirmed = window.confirm(
        'Generating a new QR code will immediately invalidate the current one. Any printed or displayed copies of the old QR will stop working. Continue?'
      );
      if (!confirmed) return;
    }
    setGenerating(true);
    setError('');
    setSuccess('');
    try {
      const token = getToken();
      const response = await axios.post(
        `${apiUrl}/api/admin/attendance-qr/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setSuccess('New QR code generated. The previous one is no longer valid.');
        fetchActive();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(response.data.message || 'Failed to generate QR code.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error generating QR code.');
    } finally {
      setGenerating(false);
    }
  };

  const qrValue = active ? `${markAttendanceBaseUrl}?token=${active.token}` : '';

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Attendance QR Code</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {loading ? (
            <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
          ) : (
            <div className="card mb-24">
              <div className="card-header border-bottom border-gray-100">
                <h5 className="mb-0">Classroom Attendance QR</h5>
                <span className="text-13 text-gray-400 d-block mt-4">
                  Display this QR code in class. Students scan it with their own phone, then type their student ID
                  number to mark today's attendance. Only one QR code is valid at a time — generating a new one
                  invalidates the previous one everywhere, including the copy shown in the student portal.
                </span>
              </div>
              <div className="card-body">
                {active ? (
                  <div className="d-flex flex-column align-items-center gap-16 py-20">
                    <div style={{ padding: 24, background: '#fff', border: '1px solid #e8f5ee', borderRadius: 16 }}>
                      <QRCodeSVG value={qrValue} size={260} />
                    </div>
                    <div className="text-center">
                      <p className="text-15 text-gray-500 mb-4">
                        Generated {new Date(active.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                      </p>
                      <p className="text-13 text-gray-400" style={{ wordBreak: 'break-all', maxWidth: 420 }}>{qrValue}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <i className="ph ph-qr-code text-gray-400" style={{ fontSize: 48 }} />
                    <p className="text-15 text-gray-500 mt-8">No QR code has been generated yet.</p>
                  </div>
                )}
                <div className="d-flex justify-content-center mt-16">
                  <button
                    type="button"
                    className="btn btn-main rounded-pill py-9"
                    onClick={handleGenerate}
                    disabled={generating}
                  >
                    {generating ? 'Generating...' : active ? 'Regenerate QR Code' : 'Generate QR Code'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminAttendanceQR;
